import React, { useMemo, useEffect, useState } from 'react';
import { StockRecommendation, MarketData, MarketSettings, PortfolioItem } from '../types';
import { StockCard } from './StockCard';
import { RefreshCw, Zap, Sparkles, Target, Loader2, Trophy, TrendingUp, TrendingDown, ArrowUpRight, BarChart3, Presentation, BookOpen, Layers } from 'lucide-react';
import { fetchBrokerIntel } from '../services/brokerIntelService';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from 'recharts';

interface PageMarketProps {
  recommendations: StockRecommendation[];
  marketData: MarketData;
  onTrade: (stock: StockRecommendation) => void;
  onRefresh: () => void;
  isLoading: boolean;
  scanProgress: number;
  enabledMarkets: MarketSettings;
  settings: any;
}

export const PageMarket: React.FC<PageMarketProps> = ({
  recommendations,
  marketData,
  onTrade,
  onRefresh,
  isLoading,
  scanProgress,
  settings
}) => {
  const [top5, setTop5] = useState<StockRecommendation[]>([]);
  const [activeSubTab, setActiveSubTab] = useState<'BTST' | 'INTRADAY'>('BTST');
  const [selectedIndex, setSelectedIndex] = useState<'NIFTY' | 'BANKNIFTY'>('NIFTY');

  useEffect(() => {
    fetchBrokerIntel(settings).then(res => setTop5(res.data));
  }, [recommendations]);

  // Extract stocks based on original categories
  const intradayRecommendations = useMemo(() => recommendations.filter(r => r.timeframe === 'INTRADAY').slice(0, 10), [recommendations]);
  const btstRecommendations = useMemo(() => recommendations.filter(r => r.timeframe === 'BTST' || !r.timeframe).slice(0, 10), [recommendations]);

  const SectionTitle = ({ icon: Icon, title, sub, color }: any) => (
    <div className="flex items-center gap-3 mb-4 px-1 mt-4 first:mt-0">
        <div className={`p-2 ${color} bg-opacity-10 rounded-xl border border-current`}>
            <Icon size={16} className={color} />
        </div>
        <div>
            <h2 className="text-sm font-black text-white uppercase tracking-wider leading-none">{title}</h2>
            <p className={`text-[8px] font-black uppercase tracking-widest mt-1 opacity-8 transition-opacity ${color}`}>{sub}</p>
        </div>
    </div>
  );

  // Index prices helper
  const idxNifty = marketData['^NSEI'] || { 
    price: 23542.45, 
    change: 114.30, 
    changePercent: 0.49,
    history: [] 
  };
  const idxBankNifty = marketData['^NSEBANK'] || { 
    price: 51280.15, 
    change: -281.40, 
    changePercent: -0.55,
    history: [] 
  };
  const idxVix = marketData['^INDIAVIX'] || { 
    price: 13.42, 
    change: 0.12, 
    changePercent: 0.90,
    history: [] 
  };

  // Option Chain Calculator
  const spotPrice = selectedIndex === 'NIFTY' ? idxNifty.price : idxBankNifty.price;
  const interval = selectedIndex === 'NIFTY' ? 50 : 100;
  const atmStrike = Math.round(spotPrice / interval) * interval;

  const strikeOptions = useMemo(() => {
    const list = [];
    for (let i = -2; i <= 2; i++) {
        const strike = atmStrike + (i * interval);
        const distancePercent = Math.abs(strike - spotPrice) / spotPrice;
        
        // Compute realistic option prices based on spot distance
        let ceProb = 1 / (1 + Math.exp((strike - spotPrice) / (interval * 1.5)));
        let peProb = 1 / (1 + Math.exp((spotPrice - strike) / (interval * 1.5)));
        
        let cePremium = (spotPrice * 0.005) * ceProb + 15;
        let pePremium = (spotPrice * 0.005) * peProb + 15;
        
        list.push({
            strike,
            cePremium: parseFloat(cePremium.toFixed(1)),
            pePremium: parseFloat(pePremium.toFixed(1)),
            isATM: strike === atmStrike
        });
    }
    return list;
  }, [spotPrice, atmStrike, interval]);

  // Handle immediate buy of an option
  const handleOptionTrade = (strike: number, optType: 'CE' | 'PE', premium: number) => {
    const code = `${selectedIndex}${strike}${optType}`;
    const lot = selectedIndex === 'NIFTY' ? 65 : 25;
    
    const optionRec: StockRecommendation = {
        symbol: `${code}.NS`,
        name: `${selectedIndex} ${strike} ${optType} Option`,
        type: 'STOCK',
        sector: 'INDEX_OPTIONS',
        currentPrice: premium,
        targetPrice: parseFloat((premium * 1.5).toFixed(1)),
        lotSize: lot,
        reason: `Automated Momentum Setup: ${selectedIndex} ${strike} Strike Target`,
        riskLevel: 'High',
        timeframe: 'INTRADAY'
    };
    onTrade(optionRec);
  };

  // Recharts history data formatter
  const chartData = useMemo(() => {
    const targetIdx = selectedIndex === 'NIFTY' ? idxNifty : idxBankNifty;
    if (targetIdx.history && targetIdx.history.length > 0) {
        return targetIdx.history.map((pt, i) => ({
            time: new Date(pt.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            price: pt.close
        }));
    }
    // Static seed data
    const base = selectedIndex === 'NIFTY' ? 23500 : 51000;
    const items = [];
    for (let i = 0; i < 20; i++) {
        items.push({
            time: `${9 + Math.floor(i / 3)}:${(i % 3) * 20}`,
            price: base + Math.sin(i * 0.3) * (base * 0.004) + (i * 4)
        });
    }
    return items;
  }, [selectedIndex, idxNifty, idxBankNifty]);

  return (
    <div className="p-4 pb-24 animate-fade-in max-w-lg mx-auto md:max-w-none">
      
      {/* Dynamic Header */}
      <div className="flex justify-between items-start mb-4">
         <div>
             <h1 className="text-2xl font-black text-white italic leading-none uppercase tracking-tighter flex items-center gap-1.5">
                 AI ROBOT TERMINAL
                 <Sparkles size={18} className="text-blue-500 animate-pulse" />
             </h1>
             <p className="text-[9px] font-black uppercase tracking-[0.2em] mt-1 flex items-center gap-1 text-slate-500">
                 <Zap size={11} className="text-yellow-500" />
                 LITERAL SIGNAL ENGINE ACTIVE
             </p>
         </div>
         <button onClick={onRefresh} disabled={isLoading} className="p-2.5 bg-slate-900 border border-slate-800 rounded-xl text-slate-400 hover:text-white transition-all active:scale-95 disabled:opacity-50">
            <RefreshCw size={16} className={isLoading ? 'animate-spin text-blue-400' : ''} />
         </button>
      </div>

      {/* Modern Loader Banner */}
      {isLoading && (
          <div className="mb-6 bg-blue-600/10 border border-blue-500/20 p-3.5 rounded-2xl flex items-center gap-4 animate-slide-up">
              <Loader2 size={16} className="text-blue-400 animate-spin" />
              <div className="flex-1">
                  <div className="flex justify-between text-[9px] font-black text-blue-400 uppercase tracking-widest mb-1">
                      <span>Syncing Live Market Feed...</span>
                      <span>{scanProgress}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500" style={{ width: `${scanProgress}%` }}></div>
                  </div>
              </div>
          </div>
      )}

      {/* Main Tabs Selection */}
      <div className="grid grid-cols-2 gap-1 bg-slate-950 p-1 rounded-xl border border-slate-800/80 mb-5">
          <button 
            id="tab-btst-ideas"
            onClick={() => setActiveSubTab('BTST')}
            className={`py-2 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeSubTab === 'BTST' 
                ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
              <Zap size={12} className={activeSubTab === 'BTST' ? 'text-yellow-400' : 'text-slate-500'} />
              <span>BTST Ideas</span>
          </button>
          
          <button 
            id="tab-intraday-ideas"
            onClick={() => setActiveSubTab('INTRADAY')}
            className={`py-2 text-[10px] font-black uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-1.5 ${
              activeSubTab === 'INTRADAY' 
                ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' 
                : 'text-slate-400 hover:text-white'
            }`}
          >
              <Target size={12} className={activeSubTab === 'INTRADAY' ? 'text-red-400 animate-pulse' : 'text-slate-500'} />
              <span>Intraday Ideas</span>
          </button>
      </div>

      {/* ----------------- SUBTAB CONTENTS ----------------- */}

      {/* BTST MODULE */}
      {activeSubTab === 'BTST' && (
          <div className="space-y-4 animate-fade-in text-slate-100">
              
              {/* TOP PICK BANNER FOR BTST */}
              {top5.filter(x => x.timeframe === 'BTST' || !x.timeframe).slice(0, 1).map(stock => (
                  <div key={`btst-tp-${stock.symbol}`} className="mb-4 bg-gradient-to-br from-indigo-950/40 to-slate-950 border border-indigo-500/20 rounded-2xl p-4 shadow-xl">
                      <div className="flex items-center gap-2 mb-2 text-indigo-400 font-black text-[9px] uppercase tracking-[0.2em]">
                          <Trophy size={12} /> Hot BTST Power Pick
                      </div>
                      <StockCard stock={stock} marketData={marketData} onTrade={onTrade} />
                  </div>
              ))}

              <SectionTitle icon={Zap} title="BTST Overnight Momentum" sub="Ready for next-day gap ups" color="text-yellow-500" />
              
              {btstRecommendations.length === 0 ? (
                  <div className="text-center p-8 bg-slate-900/10 border border-slate-800 rounded-2xl text-[10px] uppercase font-black tracking-widest text-slate-600">
                      No active BTST signals found.
                  </div>
              ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {btstRecommendations.map(item => (
                          <StockCard key={item.symbol} stock={item} marketData={marketData} onTrade={onTrade} />
                      ))}
                  </div>
              )}
          </div>
      )}

      {/* INTRADAY MODULE */}
      {activeSubTab === 'INTRADAY' && (
          <div className="space-y-4 animate-fade-in text-slate-100">
              
              {/* TOP PICK BANNER FOR INTRADAY */}
              {top5.filter(x => x.timeframe === 'INTRADAY').slice(0, 1).map(stock => (
                  <div key={`intra-tp-${stock.symbol}`} className="mb-4 bg-gradient-to-br from-red-950/30 to-slate-950 border border-red-500/20 rounded-2xl p-4 shadow-xl">
                      <div className="flex items-center gap-2 mb-2 text-red-400 font-black text-[9px] uppercase tracking-[0.2em]">
                          <Trophy size={12} /> High Velocity Intraday Target
                      </div>
                      <StockCard stock={stock} marketData={marketData} onTrade={onTrade} />
                  </div>
              ))}

              <SectionTitle icon={Target} title="Intraday Pulse Signals" sub="Executed in market hours" color="text-red-500" />
              
              {intradayRecommendations.length === 0 ? (
                  <div className="text-center p-8 bg-slate-900/10 border border-slate-800 rounded-2xl text-[10px] uppercase font-black tracking-widest text-slate-600">
                      Searching for live intraday triggers...
                  </div>
              ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {intradayRecommendations.map(item => (
                          <StockCard key={item.symbol} stock={item} marketData={marketData} onTrade={onTrade} />
                      ))}
                  </div>
              )}
          </div>
      )}

      {/* Bottom Buffer */}
      <div className="h-12"></div>
    </div>
  );
};
